import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

const THEME_STORAGE_KEY = '@questoes_app/theme';

// Cores do tema claro (background claro com fontes escuras)
export const lightTheme = {
  mode: 'light',
  colors: {
    // Backgrounds (claros)
    background: '#E8E8E8',
    surface: '#FFFFFF',
    surfaceSecondary: '#F0F2F5',

    // Header (Black Piano com degradê)
    headerBackground: '#000000', // Black Piano
    headerText: '#FF4500', // Laranja (Reddit orange) para QuizFlow
    headerSubtext: 'rgba(255, 255, 255, 0.7)',

    // Text (cores escuras para fundo claro)
    textPrimary: '#1A1A2E',
    textSecondary: '#3D3D5C',
    textTertiary: '#6B7280',
    textInverse: '#FFFFFF',

    // Borders
    border: '#E5E7EB',
    borderLight: '#F3F4F6',

    // Actions
    primary: '#8B5CF6',
    primaryLight: '#EDE9FE',
    success: '#10B981',
    successLight: '#D1FAE5',
    danger: '#EF4444',
    dangerLight: '#FEE2E2',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',

    // Cards (claros)
    cardBackground: '#FFFFFF',
    cardShadow: 'rgba(0,0,0,0.1)',

    // Input
    inputBackground: '#FFFFFF',
    inputBorder: '#D1D5DB',
    inputPlaceholder: '#9CA3AF',

    // Misc
    overlay: 'rgba(0,0,0,0.5)',
    disabled: '#D1D5DB',

    // Gradiente (não usado no modo claro)
    gradientStart: '#F5F7FA',
    gradientMiddle: '#F0F2F5',
    gradientEnd: '#E5E7EB',
  },
};

// Cores do tema escuro
export const darkTheme = {
  mode: 'dark',
  colors: {
    // Backgrounds
    background: '#000000', // Black Piano
    surface: '#121212',
    surfaceSecondary: '#1E1E1E',

    // Header
    headerBackground: '#3B18C3', // Roxo (igual ao "Como funciona?")
    headerText: '#FFFFFF',
    headerSubtext: 'rgba(255,255,255,0.8)',

    // Text
    textPrimary: '#F1F5F9',
    textSecondary: '#94A3B8',
    textTertiary: '#64748B',
    textInverse: '#000000',

    // Borders
    border: '#2A2A2A',
    borderLight: '#1A1A1A',

    // Actions
    primary: '#3B82F6',
    primaryLight: '#1E3A5F',
    success: '#10B981',
    successLight: '#064E3B',
    danger: '#EF4444',
    dangerLight: '#7F1D1D',
    warning: '#F59E0B',
    warningLight: '#78350F',

    // Cards
    cardBackground: '#121212',
    cardShadow: '#000000',

    // Input
    inputBackground: '#1E1E1E',
    inputBorder: '#333333',
    inputPlaceholder: '#666666',

    // Misc
    overlay: 'rgba(0,0,0,0.8)',
    disabled: '#404040',
  },
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false); // Light mode como padrão
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const savedTheme = await AsyncStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme !== null) {
        setIsDarkMode(savedTheme === 'dark');
      }
    } catch (error) {

    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) return undefined;

    const persistTheme = () => {
      AsyncStorage
        .setItem(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light')
        .catch(() => {});
    };

    if (typeof requestAnimationFrame === 'function') {
      let timer = null;
      const frame = requestAnimationFrame(() => {
        timer = setTimeout(persistTheme, 0);
      });
      return () => {
        if (typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(frame);
        }
        if (timer) clearTimeout(timer);
      };
    }

    const timer = setTimeout(persistTheme, 0);
    return () => clearTimeout(timer);
  }, [isDarkMode, isLoading]);

  const toggleTheme = useCallback(() => {
    setIsDarkMode((currentMode) => !currentMode);
  }, []);

  const theme = isDarkMode ? darkTheme : lightTheme;
  const value = useMemo(() => ({
    theme,
    isDarkMode,
    toggleTheme,
    isLoading,
  }), [theme, isDarkMode, toggleTheme, isLoading]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider');
  }
  return context;
};

export default ThemeContext;

